import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabase";
import { GEAR_CATEGORIES } from "../../constants";
import CalIcon from "../../components/CalIcon";
import Spinner from "../../components/Spinner";
import CategoryDetailModal from "./CategoryDetailModal";
import ContributionForm from "./ContributionForm";
import NeedsPanel from "./NeedsPanel";

function parseItem(str) {
  const idx = str.lastIndexOf("::");
  if (idx === -1) return { name: str, qty: 1 };
  return { name: str.slice(0, idx), qty: parseInt(str.slice(idx + 2)) || 1 };
}

export default function EventPage({ eventId, categoryItems }) {
  const [event,               setEvent]               = useState(null);
  const [contributions,       setContributions]       = useState([]);
  const [loading,             setLoading]             = useState(true);
  const [showForm,            setShowForm]            = useState(false);
  const [notFound,            setNotFound]            = useState(false);
  const [deleteConfirm,       setDeleteConfirm]       = useState(null);
  const [deleteInput,         setDeleteInput]         = useState("");
  const [editConfirm,         setEditConfirm]         = useState(null);
  const [editInput,           setEditInput]           = useState("");
  const [editingContribution, setEditingContribution] = useState(null);
  const [activeCategory,      setActiveCategory]      = useState(null);
  const [requests,            setRequests]            = useState([]);

  const fetchData = useCallback(async () => {
    const { data: ev } = await supabase.from("events").select("*").eq("id", eventId).single();
    if (!ev) { setNotFound(true); setLoading(false); return; }
    setEvent(ev);
    const [{ data: contribs }, { data: reqs }] = await Promise.all([
      supabase.from("contributions").select("*").eq("event_id", eventId).order("created_at"),
      supabase.from("equipment_requests").select("*").eq("event_id", eventId).order("created_at"),
    ]);
    setContributions(contribs || []);
    setRequests(reqs || []);
    setLoading(false);
  }, [eventId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function startDelete(id, name) {
    setDeleteConfirm({ id, name });
    setDeleteInput("");
  }

  async function confirmDelete() {
    await supabase.from("contributions").delete().eq("id", deleteConfirm.id);
    setDeleteConfirm(null);
    setDeleteInput("");
    fetchData();
  }

  function startEdit(contribution) {
    setEditConfirm(contribution);
    setEditInput("");
  }

  function confirmEdit() {
    setEditingContribution(editConfirm);
    setEditConfirm(null);
    setEditInput("");
  }

  if (loading) return <Spinner />;
  if (notFound) return (
    <div style={{ textAlign: "center", padding: "80px 24px", color: "#444" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>😕</div>
      <p>Evento no encontrado.</p>
    </div>
  );

  const evDate   = event.date ? new Date(event.date + "T12:00:00") : null;
  const allItems = contributions.flatMap(c => c.items || []);
  const summary  = GEAR_CATEGORIES.map(cat => ({
    ...cat,
    count: allItems.reduce((sum, i) => {
      const { name, qty } = parseItem(i);
      return cat.items.includes(name) ? sum + qty : sum;
    }, 0),
  }));

  const neededItemNames = requests
    .filter(req => {
      const covered = contributions.reduce((sum, c) => {
        for (const item of (c.items || [])) {
          const { name, qty } = parseItem(item);
          if (name === req.item_name) return sum + qty;
        }
        return sum;
      }, 0);
      return covered < req.quantity_needed;
    })
    .map(req => req.item_name);

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 16px 80px" }}>

      {/* Hero banner */}
      <div style={{ margin: "0 -16px 28px", position: "relative", height: 250, overflow: "hidden" }}>
        <img src="/banner-ap.png" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.6) 100%)" }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 16px" }}>
          <div style={{
            background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.06)", padding: "12px 14px",
            borderLeft: "3px solid #cc2200",
          }}>
            <h1 style={{
              fontFamily: "var(--font-display)", fontSize: 30, color: "#fff",
              letterSpacing: 2, margin: "0 0 6px",
              textShadow: "0 2px 12px rgba(0,0,0,0.9)",
            }}>
              {event.name}
            </h1>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12, color: "#fff" }}>
              {evDate && (
                <span style={{ display: "flex", alignItems: "center" }}>
                  <CalIcon />
                  {evDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </span>
              )}
              {event.location && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(event.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#fff", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}
                  onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                  onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
                >
                  📍 {event.location}
                </a>
              )}
            </div>
            {event.startgg_link && (
              <a href={event.startgg_link} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", marginTop: 8, fontSize: 11, color: "#cc2200", letterSpacing: 1, textDecoration: "none", borderBottom: "1px solid #cc220055" }}>
                🔗 Registro en Start.gg →
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="summary-grid">
        {summary.map(cat => (
          <div
            key={cat.id}
            className="card"
            onClick={() => cat.count > 0 && setActiveCategory(cat)}
            style={{
              textAlign: "center", padding: "16px 8px",
              cursor: cat.count > 0 ? "pointer" : "default",
              transition: "border-color 0.15s",
            }}
            onMouseEnter={e => { if (cat.count > 0) e.currentTarget.style.borderColor = "#333"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = ""; }}
          >
            <div style={{ fontSize: 22, marginBottom: 4 }}>{cat.icon}</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 900, color: cat.count > 0 ? "#00e676" : "#2a2a2a" }}>
              {cat.count}
            </div>
            <div style={{ fontSize: 10, color: "#aaa", letterSpacing: 0.5 }}>{cat.label}</div>
            {cat.count > 0 && (
              <div style={{ fontSize: 9, color: "#555", letterSpacing: 0.5, marginTop: 4 }}>ver detalle</div>
            )}
          </div>
        ))}
      </div>

      <NeedsPanel requests={requests} contributions={contributions} />

      {/* Contribution form or header */}
      {(showForm || editingContribution) ? (
        <ContributionForm
          eventId={eventId}
          existingNames={contributions
            .filter(c => editingContribution ? c.id !== editingContribution.id : true)
            .map(c => c.contributor_name.toLowerCase())}
          contribution={editingContribution}
          categoryItems={categoryItems}
          neededItems={neededItemNames}
          onSave={() => { setShowForm(false); setEditingContribution(null); fetchData(); }}
          onCancel={() => { setShowForm(false); setEditingContribution(null); }}
        />
      ) : (
        <div className="section-header" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 13, letterSpacing: 3, textTransform: "uppercase", color: "#555", margin: 0 }}>
            Aportes ({contributions.length})
          </h3>
          <button className="btn-primary" onClick={() => setShowForm(true)}>+ Anotar aporte</button>
        </div>
      )}

      {/* Contributions list */}
      {!showForm && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {contributions.length === 0 && (
            <div className="card" style={{ textAlign: "center", padding: "48px 0", color: "#333" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
              <p style={{ fontSize: 13 }}>Nadie ha anotado qué trae todavía. ¡Sé el primero!</p>
            </div>
          )}
          {contributions.map(c => (
            <div key={c.id} className="card" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17, color: "#fff", marginBottom: 8 }}>
                  {c.contributor_name}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(c.items || []).map(item => {
                    const { name, qty } = parseItem(item);
                    return (
                      <span key={item} style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 4, fontSize: 11, padding: "3px 8px", color: "#aaa" }}>
                        {qty > 1 ? <><span style={{ color: "#cc2200", fontWeight: 700 }}>×{qty}</span> {name}</> : name}
                      </span>
                    );
                  })}
                </div>
                {c.notes && <p style={{ fontSize: 12, color: "#888", marginTop: 8, marginBottom: 0 }}>💬 {c.notes}</p>}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                <button
                  onClick={() => startEdit(c)}
                  title="Editar"
                  style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 4, color: "#888", cursor: "pointer", padding: "6px 8px", display: "flex", alignItems: "center", transition: "border-color 0.15s, color 0.15s, background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#666"; e.currentTarget.style.background = "#252525"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#888"; e.currentTarget.style.borderColor = "#333"; e.currentTarget.style.background = "#1a1a1a"; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  onClick={() => startDelete(c.id, c.contributor_name)}
                  title="Eliminar"
                  style={{ background: "#1a0a0a", border: "1px solid #4a1a1a", borderRadius: 4, color: "#cc2200", cursor: "pointer", padding: "6px 8px", display: "flex", alignItems: "center", transition: "border-color 0.15s, color 0.15s, background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#ff3a1a"; e.currentTarget.style.borderColor = "#cc2200"; e.currentTarget.style.background = "#2a0a0a"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#cc2200"; e.currentTarget.style.borderColor = "#4a1a1a"; e.currentTarget.style.background = "#1a0a0a"; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit confirm modal */}
      {editConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "0 20px" }}>
          <div className="card" style={{ maxWidth: 400, width: "100%", padding: "28px 24px" }}>
            <p style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Editar el aporte de</p>
            <p style={{ fontFamily: "var(--font-display)", fontSize: 22, color: "#fff", marginBottom: 20, letterSpacing: 1 }}>
              {editConfirm.contributor_name}
            </p>
            <p style={{ fontSize: 12, color: "#999", marginBottom: 8 }}>Escribe el nombre para confirmar:</p>
            <input
              className="input"
              placeholder={editConfirm.contributor_name}
              value={editInput}
              onChange={e => setEditInput(e.target.value)}
              autoFocus
              style={{ marginBottom: 16 }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn-primary"
                style={{ flex: 1, opacity: editInput.trim().toLowerCase() === editConfirm.contributor_name.toLowerCase() ? 1 : 0.3 }}
                disabled={editInput.trim().toLowerCase() !== editConfirm.contributor_name.toLowerCase()}
                onClick={confirmEdit}
              >
                Editar
              </button>
              <button className="btn-ghost" onClick={() => { setEditConfirm(null); setEditInput(""); }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "0 20px" }}>
          <div className="card" style={{ maxWidth: 400, width: "100%", padding: "28px 24px" }}>
            <p style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Eliminar el aporte de</p>
            <p style={{ fontFamily: "var(--font-display)", fontSize: 22, color: "#fff", marginBottom: 20, letterSpacing: 1 }}>
              {deleteConfirm.name}
            </p>
            <p style={{ fontSize: 12, color: "#999", marginBottom: 8 }}>Escribe el nombre para confirmar:</p>
            <input
              className="input"
              placeholder={deleteConfirm.name}
              value={deleteInput}
              onChange={e => setDeleteInput(e.target.value)}
              autoFocus
              style={{ marginBottom: 16 }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn-danger"
                style={{ flex: 1, padding: 10, opacity: deleteInput.trim().toLowerCase() === deleteConfirm.name.toLowerCase() ? 1 : 0.3 }}
                disabled={deleteInput.trim().toLowerCase() !== deleteConfirm.name.toLowerCase()}
                onClick={confirmDelete}
              >
                Eliminar
              </button>
              <button className="btn-ghost" onClick={() => { setDeleteConfirm(null); setDeleteInput(""); }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {activeCategory && (
        <CategoryDetailModal
          cat={activeCategory}
          contributions={contributions}
          onClose={() => setActiveCategory(null)}
        />
      )}
    </div>
  );
}
